import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2, Shield, Trash2, TriangleAlert, RotateCcw, Flag, Users, Search, ShieldCheck, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "@/lib/helpers";
import { CATEGORY_MAP } from "@/lib/categories";
import { Input } from "@/components/ui/input";
import { formatApiError } from "@/lib/api";

const REASON_LABELS = {
  unkind: "Unkind or degrading",
  privacy: "Shares private info",
  spam: "Spam or advertising",
  misinformation: "Misinformation",
  political: "Political content",
  off_topic: "Not a cost-saving tip",
  other: "Something else",
};

export default function Admin() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [memberSearch, setMemberSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, u] = await Promise.all([
        api.get("/admin/reports", { params: statusFilter === "all" ? {} : { status: statusFilter } }),
        api.get("/admin/users"),
      ]);
      setReports(r.data.reports);
      setUsers(u.data.users);
    } catch (err) {
      toast.error("Could not load admin data");
    }
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  const removeTip = async (id) => {
    try {
      await api.delete(`/admin/tips/${id}`);
      toast.success("Tip removed");
      load();
    } catch { toast.error("Failed"); }
  };

  const dismissReport = async (id) => {
    try {
      await api.post(`/admin/reports/${id}/dismiss`);
      toast.success("Report dismissed");
      load();
    } catch { toast.error("Failed"); }
  };

  const warnUser = async (id) => {
    try {
      const { data } = await api.post(`/admin/users/${id}/warn`);
      if (data.user.suspended) toast.success("User has been suspended (3 warnings)");
      else toast.success(`Warning issued (${data.user.warnings_count}/3)`);
      load();
    } catch { toast.error("Failed"); }
  };

  const unsuspend = async (id) => {
    try {
      await api.post(`/admin/users/${id}/unsuspend`);
      toast.success("User reinstated");
      load();
    } catch { toast.error("Failed"); }
  };

  const makeAdmin = async (id, name) => {
    try {
      await api.post(`/admin/users/${id}/make-admin`);
      toast.success(`${name} is now an admin`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Could not promote"));
    }
  };

  const removeAdmin = async (id, name) => {
    try {
      await api.post(`/admin/users/${id}/remove-admin`);
      toast.success(`${name} is no longer an admin`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Could not demote"));
    }
  };

  const adminCount = users.filter((u) => u.role === "admin").length;
  const term = memberSearch.trim().toLowerCase();
  const filteredUsers = term
    ? users.filter((m) =>
        `${m.first_name} ${m.last_name}`.toLowerCase().includes(term) ||
        (m.email || "").toLowerCase().includes(term) ||
        (m.zip_code || "").toLowerCase().includes(term)
      )
    : users;
  const pendingCount = reports.filter((r) => r.status === "pending").length;

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="text-sm font-semibold text-[#2A9D8F] tracking-widest uppercase flex items-center gap-2">
            <Shield className="w-4 h-4" /> Admin
          </p>
          <h1 className="mt-2 font-heading text-4xl sm:text-5xl font-bold text-[#1B2A4A] leading-tight">
            Keep it <span className="text-[#E8621A]">warm & safe.</span>
          </h1>
          <p className="mt-3 text-[#1B2A4A]/70">
            Review reports, remove violating posts, and issue warnings when needed.
          </p>
        </div>
        <Button onClick={load} variant="ghost" data-testid="admin-refresh" className="rounded-full">
          <RotateCcw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      <Tabs defaultValue="reports">
        <TabsList className="rounded-full bg-white border border-[#1B2A4A]/8 p-1 h-11">
          <TabsTrigger value="reports" data-testid="tab-reports" className="rounded-full data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white px-4 gap-2">
            <Flag className="w-3.5 h-3.5" /> Reports
            {pendingCount > 0 && <span className="rounded-full bg-[#E76F51] text-white text-[10px] px-1.5 py-0.5">{pendingCount}</span>}
          </TabsTrigger>
          <TabsTrigger value="members" data-testid="tab-members" className="rounded-full data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white px-4 gap-2">
            <Users className="w-3.5 h-3.5" /> Members ({users.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="reports" className="mt-4 space-y-4">
          <div className="flex gap-2 flex-wrap">
            {["pending", "resolved", "dismissed", "all"].map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                data-testid={`filter-report-${s}`}
                className={`rounded-full px-4 py-1.5 text-xs font-semibold capitalize transition-colors duration-200 ${
                  statusFilter === s ? "bg-[#1B2A4A] text-white" : "bg-white text-[#1B2A4A]/70 border border-[#1B2A4A]/10 hover:bg-[#1B2A4A]/5"
                }`}
              >
                {s}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#E8621A]" /></div>
          ) : reports.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-3xl border border-dashed border-[#1B2A4A]/15">
              <Flag className="w-8 h-8 mx-auto text-[#1B2A4A]/25" />
              <p className="mt-2 text-sm text-[#1B2A4A]/60">No reports here — the community is looking clean. 🌿</p>
            </div>
          ) : (
            reports.map((r) => {
              const cat = r.tip ? (CATEGORY_MAP[r.tip.category] || CATEGORY_MAP.general) : null;
              return (
                <div key={r.id} data-testid={`report-${r.id}`} className="rounded-3xl bg-white border border-[#1B2A4A]/8 p-5 sm:p-6">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-widest ${
                      r.status === "pending" ? "bg-[#E76F51]/12 text-[#E76F51]" :
                      r.status === "resolved" ? "bg-[#2A9D8F]/12 text-[#2A9D8F]" :
                      "bg-[#1B2A4A]/10 text-[#1B2A4A]/60"
                    }`}>{r.status}</span>
                    <span className="text-xs text-[#1B2A4A]/60">{REASON_LABELS[r.reason] || r.reason}</span>
                    <span className="text-xs text-[#1B2A4A]/40">·</span>
                    <span className="text-xs text-[#1B2A4A]/60">Reported by {r.reporter_name}</span>
                    <span className="text-xs text-[#1B2A4A]/40">·</span>
                    <span className="text-xs text-[#1B2A4A]/60">{formatDate(r.created_at)}</span>
                  </div>

                  {r.tip ? (
                    <div className="rounded-2xl bg-[#F7F5F2] p-4 mb-3">
                      {cat && (
                        <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold mb-2"
                          style={{ background: cat.bg, color: cat.color }}>
                          {cat.label}
                        </span>
                      )}
                      <p className="text-sm text-[#1B2A4A]/85">"{r.tip.text}"</p>
                    </div>
                  ) : (
                    <p className="text-xs italic text-[#1B2A4A]/50 mb-3">Tip already removed</p>
                  )}

                  {r.details && (
                    <p className="text-sm text-[#1B2A4A]/70 mb-3">
                      <span className="font-semibold">Reporter's note:</span> {r.details}
                    </p>
                  )}

                  {r.author && (
                    <p className="text-xs text-[#1B2A4A]/60 mb-4">
                      Author: <span className="font-semibold text-[#1B2A4A]">{r.author.first_name} {r.author.last_name}</span>
                      {" · "}
                      <span className="text-[#E76F51] font-semibold">{r.author.warnings_count}/3 warnings</span>
                      {r.author.suspended && <span className="ml-1 rounded-full bg-[#E76F51] text-white text-[10px] px-1.5 py-0.5 uppercase">suspended</span>}
                    </p>
                  )}

                  {r.status === "pending" && (
                    <div className="flex flex-wrap gap-2 pt-2 border-t border-[#1B2A4A]/8">
                      {r.tip && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" data-testid={`admin-remove-tip-${r.id}`} className="rounded-full bg-[#E76F51] hover:bg-[#E76F51]/90 text-white">
                              <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove tip
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="rounded-3xl">
                            <AlertDialogHeader>
                              <AlertDialogTitle>Remove this tip?</AlertDialogTitle>
                              <AlertDialogDescription>The tip will be deleted and the report resolved.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => removeTip(r.tip.id)} className="rounded-full bg-[#E76F51] hover:bg-[#E76F51]/90">Remove</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}

                      {r.author && !r.author.suspended && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="outline" data-testid={`admin-warn-${r.id}`} className="rounded-full border-[#F4A261] text-[#B8752E] hover:bg-[#F4A261]/10">
                              <TriangleAlert className="w-3.5 h-3.5 mr-1" /> Warn author
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="rounded-3xl">
                            <AlertDialogHeader>
                              <AlertDialogTitle>Warn {r.author.first_name}?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Current warnings: {r.author.warnings_count}/3. At 3 warnings, the account is automatically suspended.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => warnUser(r.author.id)} className="rounded-full bg-[#F4A261] hover:bg-[#F4A261]/90 text-[#1B2A4A]">Issue warning</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}

                      <Button size="sm" variant="ghost" data-testid={`admin-dismiss-${r.id}`} onClick={() => dismissReport(r.id)} className="rounded-full text-[#1B2A4A]/60">
                        Dismiss report
                      </Button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </TabsContent>

        <TabsContent value="members" className="mt-4">
          <div className="mb-3 relative max-w-md">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1B2A4A]/40" />
            <Input
              value={memberSearch}
              onChange={(e) => setMemberSearch(e.target.value)}
              placeholder="Search by name, email, or zip..."
              data-testid="admin-member-search"
              className="pl-10 h-11 rounded-full bg-white border-[#1B2A4A]/15"
            />
          </div>
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#E8621A]" /></div>
          ) : (
            <div className="rounded-3xl bg-white border border-[#1B2A4A]/8 overflow-hidden">
              <div className="grid grid-cols-12 px-5 py-3 bg-[#F7F5F2] text-xs font-semibold uppercase tracking-widest text-[#1B2A4A]/60">
                <div className="col-span-4">Member</div>
                <div className="col-span-3">Email</div>
                <div className="col-span-1 text-center">Tips</div>
                <div className="col-span-2 text-center">Warnings</div>
                <div className="col-span-2 text-right">Actions</div>
              </div>
              {filteredUsers.map((m) => (
                <div key={m.id} data-testid={`member-row-${m.id}`} className="grid grid-cols-12 px-5 py-3 items-center border-t border-[#1B2A4A]/8 text-sm">
                  <div className="col-span-4 flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-[#1B2A4A]">{m.first_name} {m.last_name}</span>
                    {m.role === "admin" && <span className="text-[10px] bg-[#2A9D8F]/15 text-[#2A9D8F] px-1.5 py-0.5 rounded-full font-bold uppercase">Admin</span>}
                    {m.suspended && <span className="text-[10px] bg-[#E76F51] text-white px-1.5 py-0.5 rounded-full font-bold uppercase">Suspended</span>}
                  </div>
                  <div className="col-span-3 text-[#1B2A4A]/70 truncate text-xs">{m.email}</div>
                  <div className="col-span-1 text-center font-semibold text-[#1B2A4A]">{m.tips_count}</div>
                  <div className={`col-span-2 text-center font-bold ${m.warnings_count >= 2 ? "text-[#E76F51]" : m.warnings_count >= 1 ? "text-[#F4A261]" : "text-[#1B2A4A]/50"}`}>
                    {m.warnings_count}/3
                  </div>
                  <div className="col-span-2 flex justify-end gap-1 flex-wrap">
                    {m.role === "admin" ? (
                      <Button
                        size="sm" variant="ghost"
                        data-testid={`member-remove-admin-${m.id}`}
                        disabled={adminCount <= 1}
                        title={adminCount <= 1 ? "Can't remove the last admin" : "Remove admin role"}
                        onClick={() => removeAdmin(m.id, m.first_name)}
                        className="rounded-full text-[#E76F51] text-xs disabled:opacity-40"
                      >
                        <ShieldOff className="w-3.5 h-3.5 mr-1" /> Remove admin
                      </Button>
                    ) : (
                      !m.suspended && (
                        <Button
                          size="sm" variant="ghost"
                          data-testid={`member-make-admin-${m.id}`}
                          onClick={() => makeAdmin(m.id, m.first_name)}
                          className="rounded-full text-[#2A9D8F] text-xs"
                        >
                          <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Make admin
                        </Button>
                      )
                    )}
                    {m.role !== "admin" && (
                      m.suspended ? (
                        <Button size="sm" variant="ghost" data-testid={`member-unsuspend-${m.id}`} onClick={() => unsuspend(m.id)} className="rounded-full text-[#2A9D8F] text-xs">
                          Reinstate
                        </Button>
                      ) : (
                        <Button size="sm" variant="ghost" data-testid={`member-warn-${m.id}`} onClick={() => warnUser(m.id)} className="rounded-full text-[#F4A261] text-xs">
                          Warn
                        </Button>
                      )
                    )}
                  </div>
                </div>
              ))}
              {filteredUsers.length === 0 && (
                <div className="text-center py-10 text-sm text-[#1B2A4A]/60">No members match "{memberSearch}".</div>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
